import { useState } from "react";
import { useLocation } from "wouter";
import { authClient } from "@/lib/authClient";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Mail, RefreshCw } from "lucide-react";

export default function SignupSuccess() {
  // Read the email from the query string (?email=…)
  const search = typeof window !== "undefined" ? window.location.search : "";
  const params = new URLSearchParams(search);
  const email =
    params.get("email") ||
    sessionStorage.getItem("gp.signupEmail") ||
    "your email";

  const [, setLocation] = useLocation();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState("");

  const handleResend = async () => {
    if (email === "your email") {
      setResendError("Please sign in to request a new verification link.");
      return;
    }
    setResending(true);
    setResendError("");
    setResent(false);
    try {
      const result = await authClient.sendVerificationEmail({
        email,
        callbackURL: "/profile-setup",
      });
      if (result.error) {
        setResendError(
          result.error.message ?? "Failed to resend. Please try again."
        );
        return;
      }
      setResent(true);
    } catch (err: any) {
      setResendError(err?.message ?? "Failed to resend. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      {/* Background glow */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-neon/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="relative w-full max-w-md text-center">
        {/* Logo */}
        <div className="flex items-center gap-2.5 justify-center mb-10">
          <div className="w-10 h-10 rounded-lg bg-neon/15 border border-neon/30 flex items-center justify-center">
            <span className="text-neon font-extrabold text-base tracking-tighter">
              GP
            </span>
          </div>
          <span className="text-2xl font-extrabold tracking-tight uppercase">
            <span className="text-white">Game</span>
            <span className="text-neon">Point</span>
          </span>
        </div>

        {/* Success icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-neon/10 border border-neon/30 flex items-center justify-center">
            <Mail className="w-9 h-9 text-neon" />
          </div>
        </div>

        <h1 className="text-2xl font-extrabold text-white uppercase mb-3">
          Check your inbox!
        </h1>
        <p className="text-slate-400 text-sm leading-relaxed mb-2">
          We sent a confirmation link to
        </p>
        <p className="text-white font-semibold mb-8 break-all">{email}</p>

        <div className="bg-neon/5 border border-neon/20 rounded-xl p-5 mb-8 text-left space-y-3">
          {[
            "Open the email from GamePoint",
            'Click "Verify My Email"',
            "Complete your profile setup",
            "Start competing & earning",
          ].map((step, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-neon/20 border border-neon/30 flex items-center justify-center text-neon text-xs font-bold shrink-0">
                {i + 1}
              </div>
              <span className="text-sm text-slate-300">{step}</span>
            </div>
          ))}
        </div>

        {/* Resend */}
        {resent ? (
          <div className="flex items-center justify-center gap-2 text-sm text-neon mb-6">
            <CheckCircle2 className="w-4 h-4" />
            Verification email resent!
          </div>
        ) : (
          <div className="mb-6">
            <p className="text-xs text-slate-500 mb-2">
              Didn't get it? Check your spam folder or
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResend}
              disabled={resending}
              className="text-neon hover:text-neon/80 hover:bg-neon/10 gap-2"
            >
              {resending ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Resending…
                </>
              ) : (
                <>
                  <RefreshCw className="w-3.5 h-3.5" /> Resend verification
                  email
                </>
              )}
            </Button>
            {resendError && (
              <p className="text-xs text-destructive mt-2">{resendError}</p>
            )}
          </div>
        )}

        <button
          onClick={() => setLocation("/login")}
          className="text-xs text-slate-500 hover:text-slate-400 transition-colors"
        >
          Back to Sign In
        </button>
      </div>
    </div>
  );
}
